/* eslint-disable import/prefer-default-export */
import { defineStore } from 'pinia';
import axios from 'axios';
import { useSnackbarHandler } from '@/composables/useSnackbarHandler';
import { sentryService } from '@/services/SentryService';
export const useMaxioSubscriptionStore = defineStore('maxioSubscriptionStore', {
  state: () => {
    const {
      handleError,
      handleSuccess
    } = useSnackbarHandler();
    return {
      saving: false,
      alerts: {},
      handleError,
      handleSuccess,
      subscription: [],
      subscriptionProducts: [],
      accounts: [],
      subscriptionComponent: [],
      invoices: []
    };
  },
  actions: {
    async applyCode(code) {
      const result = await axios({
        url: `/subscription/apply/${code}`,
        method: 'POST'
      });
      return result;
    },
    async verifyCoupon(productId, couponCode) {
      try {
        const response = await axios({
          url: `/subscription/coupons/verify/${productId}?couponCode=${encodeURIComponent(couponCode)}`,
          method: 'GET'
        });
        return response.data;
      } catch (error) {
        this.handleError(error);
        throw error;
      }
    },
    async getSubscription() {
      try {
        const response = await axios({
          url: `/subscription`
        });
        this.$patch({
          subscription: response.data
        });
        return response.data;
      } catch (error) {
        this.handleError(error);
        return null;
      }
    },
    async createSubscriptionComponent(params) {
      try {
        // Build request body per new API contract
        const subs = this.subscription || {};
        const plan = subs && subs.plan || {};
        const body = {
          productId: params && (params.productId || params.id) || undefined,
          seats: params && params.seats !== undefined ? params.seats : subs.seatsRequired || plan.currentPaidSeats || plan.seats || 1,
          coupon: params && (params.coupon || params.couponCode) || subs.couponCode || '',
          email: params && params.email || subs.billingEmail || subs.email || ''
        };
        const response = await axios({
          url: `/subscription/create`,
          method: 'POST',
          data: body
        });
        this.$patch({
          subscription: response.data
        });
        return response.data;
      } catch (error) {
        this.handleError(error);
        return null;
      }
    },
    async updateSubscriptions(params, customSuccessMessage = null) {
      try {
        const response = await axios({
          url: `/subscription`,
          method: 'PUT',
          data: params
        });
        this.$patch({
          subscription: response.data
        });
        // Surface success toast here so callers don't all need to duplicate messaging.
        if (customSuccessMessage) {
          this.handleSuccess(customSuccessMessage);
        } else {
          const planLabel = response.data && response.data.plan && response.data.plan.label || response.data && response.data.product && response.data.product.label;
          if (planLabel) {
            this.handleSuccess(`Successfully updated to ${planLabel}`);
          } else {
            this.handleSuccess('Subscription updated successfully');
          }
        }
        return response.data;
      } catch (error) {
        this.handleError(error);
        return null;
      }
    },
    // Features are read once at startup, by both useMeStore and Angular's
    // UserStore.me, so only a full reload unlocks or locks them for the new plan.
    async changePlan(productId) {
      const updated = await this.updateSubscriptions({
        productId
      });
      if (updated) {
        window.location.reload();
      }
      return updated;
    },
    async listSubscriptionProducts() {
      try {
        const response = await axios({
          url: `/subscription/products`
        });
        this.$patch({
          subscriptionProducts: response.data
        });
        return response.data;
      } catch (error) {
        this.handleError(error);
        return null;
      }
    },
    async listAccounts() {
      try {
        const response = await axios({
          url: `/accounts`
        });
        this.$patch({
          accounts: response.data
        });
        return response.data;
      } catch (error) {
        this.handleError(error);
        return null;
      }
    },
    async createSubscription(subscriptionData) {
      try {
        const response = await axios({
          url: `/subscription`,
          method: 'POST',
          data: subscriptionData
        });
        this.$patch({
          subscription: response.data
        });
        return response.data;
      } catch (error) {
        this.handleError(error);
        return null;
      }
    },
    async getSubscriptionInvoices() {
      try {
        const response = await axios({
          url: `/subscription/invoices`
        });
        this.$patch({
          invoices: response.data
        });
        return response.data;
      } catch (error) {
        this.handleError(error);
        return null;
      }
    },
    async cancelSubscription(selectedReason, customReason) {
      try {
        const body = {
          selectedReason,
          customReason
        };
        await axios({
          url: `/subscription/usercancel`,
          method: 'POST',
          data: body
        });
        this.handleSuccess('Subscription cancelled successfully');
        await this.getSubscription();
      } catch (error) {
        this.handleError(error);
      }
    },
    async notifyCancelIntent() {
      try {
        await axios({
          url: `/subscription/cancel-intent`,
          method: 'POST'
        });
      } catch (error) {
        // Fire-and-forget — don't block the cancellation flow, but log to Sentry
        sentryService.captureException(error, {
          feature: 'cancel-intent-notification'
        });
      }
    },
    async verifyBankAccount(deposit1InCents, deposit2InCents) {
      try {
        const response = await axios.post('/subscription/verify', {
          deposit1InCents,
          deposit2InCents
        });
        this.$patch({
          subscription: response.data
        });
        return response.data;
      } catch (error) {
        this.handleError(error);
        return null;
      }
    },
    getSubscriptionPlanFromProduct(productId) {
      return this.subscriptionProducts.products.find(product => product.id === productId);
    }
  },
  getters: {
    subscriptionState: state => state.subscription && state.subscription.state || 'not status',
    isActiveSubscription: state => {
      const sub = state.subscription || {};
      return sub.state === 'active' || sub.state === 'past_due' || sub.state === 'unpaid';
    },
    isAwaitingSubscription: state => {
      const sub = state.subscription || {};
      return sub.state === 'awaiting_signup';
    },
    isCanceledSubscription: state => {
      const sub = state.subscription || {};
      return sub.state === 'canceled';
    },
    allowSelfServiceCancellation: state => {
      return state.subscription?.allowSelfServiceCancellation === true;
    },
    isAchVerified: state => {
      const sub = state.subscription || {};
      return sub.isAchVerified;
    }
  }
});